import { Controller } from "@hotwired/stimulus";

// Manages Shopee import page: persistent selections across "Load More",
// client-side shop name filtering, and form submission with hidden inputs.
export default class extends Controller {
  static targets = [
    "checkbox",
    "productRow",
    "productCard",
    "desktopBody",
    "mobileList",
    "loadMoreBtn",
    "loadMoreLoading",
    "loadMoreSection",
    "selectedCount",
    "importBtn",
    "stickyBar",
    "shopFilter",
  ];

  connect() {
    // Map<payloadJSON, true> — tracks selected product payloads
    this.selectedProducts = new Map();
    this.currentPage = parseInt(this.element.dataset.currentPage || "1", 10);
    this.shopFilterTimer = null;
    this.updateUI();
  }

  // --- Selection management ---

  toggleSelection(event) {
    const checkbox = event.currentTarget;
    const payload = checkbox.dataset.payload;

    if (checkbox.checked) {
      this.selectedProducts.set(payload, true);
    } else {
      this.selectedProducts.delete(payload);
    }

    // Sync paired checkbox (desktop <-> mobile share same payload)
    this.syncCheckboxes(payload, checkbox.checked);
    this.updateUI();
  }

  toggleAll(event) {
    const checked = event.currentTarget.checked;

    // Only toggle visible (not hidden by shop filter) checkboxes
    this.checkboxTargets.forEach((cb) => {
      if (cb.closest("[style*='display: none']")) return;
      cb.checked = checked;
      const payload = cb.dataset.payload;
      if (checked) {
        this.selectedProducts.set(payload, true);
      } else {
        this.selectedProducts.delete(payload);
      }
    });

    this.updateUI();
  }

  syncCheckboxes(payload, checked) {
    this.checkboxTargets.forEach((cb) => {
      if (cb.dataset.payload === payload) {
        cb.checked = checked;
      }
    });
  }

  reapplySelections() {
    this.checkboxTargets.forEach((cb) => {
      const payload = cb.dataset.payload;
      if (this.selectedProducts.has(payload)) {
        cb.checked = true;
      }
    });
  }

  // --- Load More ---

  async loadMore(event) {
    event.preventDefault();

    const nextPage = this.currentPage + 1;
    const url = new URL(this.element.dataset.loadMoreUrl, window.location.origin);
    url.searchParams.set("page", nextPage);

    this.showLoadMoreLoading();

    try {
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      Turbo.renderStreamMessage(html);

      this.currentPage = nextPage;

      // Re-apply selections to newly added checkboxes after Turbo processes
      requestAnimationFrame(() => {
        this.reapplySelections();
        this.updateUI();
      });
    } catch (error) {
      console.error("Error loading more Shopee products:", error);
      this.showLoadMoreError();
    } finally {
      this.hideLoadMoreLoading();
    }
  }

  showLoadMoreLoading() {
    if (this.hasLoadMoreBtnTarget) this.loadMoreBtnTarget.classList.add("hidden");
    if (this.hasLoadMoreLoadingTarget) this.loadMoreLoadingTarget.classList.remove("hidden");
  }

  hideLoadMoreLoading() {
    if (this.hasLoadMoreBtnTarget) this.loadMoreBtnTarget.classList.remove("hidden");
    if (this.hasLoadMoreLoadingTarget) this.loadMoreLoadingTarget.classList.add("hidden");
  }

  showLoadMoreError() {
    if (!this.hasLoadMoreSectionTarget) return;
    const msg = document.createElement("p");
    msg.className = "text-sm text-error mt-2";
    msg.textContent = "Erro ao carregar mais produtos. Tente novamente.";
    this.loadMoreSectionTarget.appendChild(msg);
    setTimeout(() => msg.remove(), 5000);
  }

  // Called from turbo_stream response to hide "Load More" when no more pages
  hideLoadMore() {
    if (this.hasLoadMoreSectionTarget) {
      this.loadMoreSectionTarget.classList.add("hidden");
    }
  }

  // --- Shop name filter (client-side) ---

  filterByShop() {
    clearTimeout(this.shopFilterTimer);
    this.shopFilterTimer = setTimeout(() => {
      this.applyShopFilter();
    }, 250);
  }

  applyShopFilter() {
    const query = this.hasShopFilterTarget
      ? this.shopFilterTarget.value.trim().toLowerCase()
      : "";

    // Filter desktop rows
    this.productRowTargets.forEach((row) => {
      const shopName = row.dataset.shopName || "";
      row.style.display =
        query === "" || shopName.includes(query) ? "" : "none";
    });

    // Filter mobile cards
    this.productCardTargets.forEach((card) => {
      const shopName = card.dataset.shopName || "";
      card.style.display =
        query === "" || shopName.includes(query) ? "" : "none";
    });
  }

  // --- Form submission ---

  submitImport(event) {
    event.preventDefault();

    const form = this.element.querySelector("#shopee-import-form");
    if (!form) return;

    // Clear any existing hidden inputs
    form.querySelectorAll('input[name="selected_products[]"]').forEach((el) => el.remove());

    // Inject hidden inputs for all selected products
    this.selectedProducts.forEach((_value, payload) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "selected_products[]";
      input.value = payload;
      form.appendChild(input);
    });

    form.submit();
  }

  // --- UI updates ---

  updateUI() {
    const count = this.selectedProducts.size;

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = count;
    }

    if (this.hasImportBtnTarget) {
      this.importBtnTarget.disabled = count === 0;
    }

    if (this.hasStickyBarTarget) {
      if (count > 0) {
        this.stickyBarTarget.classList.remove("translate-y-full", "opacity-0");
        this.stickyBarTarget.classList.add("translate-y-0", "opacity-100");
      } else {
        this.stickyBarTarget.classList.add("translate-y-full", "opacity-0");
        this.stickyBarTarget.classList.remove("translate-y-0", "opacity-100");
      }
    }
  }
}
